import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetReplyDraft,
  useSaveReplyDraft,
  useDeleteReplyDraft,
  getGetReplyDraftQueryKey,
  type ReplyDraft,
} from "@workspace/api-client-react";

/**
 * Hook that wires the inline reply panel on `/app/inbox/:id` and
 * `/app/gmail/:id` to the per-user `reply_drafts` table on the
 * server, so a half-typed (or AI-generated) reply survives a page
 * refresh / a hop into another tab.
 *
 * Behavior:
 *   - On mount, fetches the saved draft for `(kind, ref)` and calls
 *     `onRestore` once with it (or `null` when none exists). The
 *     consumer uses `onRestore` to re-open the inline reply panel
 *     and seed the form state.
 *   - Whenever `subject`, `body`, or `isAiDrafted` change while the
 *     panel is open, schedules a debounced PUT (600ms) so the
 *     server-side row stays in sync.
 *   - Exposes `clearDraft()` for the explicit "Discard draft" button
 *     and the post-send cleanup. Both reset the local "last saved"
 *     tracking so a subsequent edit doesn't compare against stale
 *     data.
 *
 * The hook deliberately compares each save against the last persisted
 * subject/body/isAiDrafted so re-renders that don't actually change
 * the draft (e.g. typing then deleting back to the same value) don't
 * generate unnecessary network traffic.
 */
export function useReplyDraftPersistence(opts: {
  kind: "inbound" | "gmail";
  ref: string;
  enabled: boolean;
  open: boolean;
  subject: string;
  body: string;
  isAiDrafted: boolean;
  onRestore: (draft: ReplyDraft | null) => void;
}) {
  const {
    kind,
    ref,
    enabled,
    open,
    subject,
    body,
    isAiDrafted,
    onRestore,
  } = opts;
  const queryClient = useQueryClient();

  // Disable React Query's automatic refetch-on-focus / refetch-on-mount
  // so we don't clobber the user's just-typed text with a stale server
  // copy when they tab back to the page. We only need the initial fetch.
  const draftQuery = useGetReplyDraft(kind, ref, {
    query: {
      queryKey: getGetReplyDraftQueryKey(kind, ref),
      enabled: enabled && ref.length > 0,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: Infinity,
    },
  });

  const saveMutation = useSaveReplyDraft();
  const deleteMutation = useDeleteReplyDraft();

  // True once we've seen the initial fetch resolve and called
  // `onRestore` — gates the auto-save effect so we don't accidentally
  // PUT before we know whether a draft already existed.
  const restoredRef = useRef(false);
  // The last subject/body/isAiDrafted we persisted to the server.
  // The auto-save effect compares against this to skip no-op saves.
  const lastSavedRef = useRef<{
    subject: string;
    body: string;
    isAiDrafted: boolean;
  } | null>(null);

  // Reset the per-thread refs whenever the (kind, ref) target changes.
  // Today the inbox and Gmail detail pages remount on navigation so a
  // brand-new hook instance always handles each thread, but a parent
  // doing a route-only param update (e.g. switching :id without
  // unmounting) would otherwise re-use stale "already restored" state
  // from the previous thread and silently skip the new thread's
  // initial restore. Cheap belt-and-braces.
  useEffect(() => {
    restoredRef.current = false;
    lastSavedRef.current = null;
  }, [kind, ref]);

  // Restore the saved draft (if any) once the initial fetch settles —
  // success OR error. We deliberately unblock autosave on error too so
  // a transient read failure doesn't silently disable persistence for
  // the rest of the page mount; the next successful PUT will overwrite
  // whatever's on the server. Subsequent re-renders are no-ops because
  // `restoredRef` stays true for the lifetime of the page mount.
  useEffect(() => {
    if (restoredRef.current) return;
    if (!draftQuery.isSuccess && !draftQuery.isError) return;
    const data = draftQuery.isSuccess ? (draftQuery.data ?? null) : null;
    restoredRef.current = true;
    if (data) {
      // Seed `lastSavedRef` BEFORE the consumer calls back into our
      // state, so the auto-save effect's first comparison sees "no
      // change" and skips the redundant initial PUT.
      lastSavedRef.current = {
        subject: data.subject,
        body: data.body,
        isAiDrafted: data.isAiDrafted,
      };
    }
    onRestore(data);
  }, [
    draftQuery.isSuccess,
    draftQuery.isError,
    draftQuery.data,
    onRestore,
  ]);

  // Debounced auto-save. Fires only when the panel is open, the
  // initial restore has happened, and the current values differ from
  // what we last successfully saved.
  useEffect(() => {
    if (!enabled) return;
    if (!restoredRef.current) return;
    if (!open) return;

    const last = lastSavedRef.current;
    const trimmedSubject = subject;
    const trimmedBody = body;
    if (
      last &&
      last.subject === trimmedSubject &&
      last.body === trimmedBody &&
      last.isAiDrafted === isAiDrafted
    ) {
      return;
    }

    // Skip when there's nothing meaningful to save and no row exists
    // yet — saves an empty INSERT for a user who just opens and
    // immediately closes the panel without typing.
    const hasContent =
      trimmedSubject.trim().length > 0 || trimmedBody.trim().length > 0;
    if (!hasContent && !last) return;

    const timer = setTimeout(() => {
      saveMutation.mutate(
        {
          kind,
          ref,
          data: {
            subject: trimmedSubject,
            body: trimmedBody,
            isAiDrafted,
          },
        },
        {
          onSuccess: (saved) => {
            lastSavedRef.current = {
              subject: saved.subject,
              body: saved.body,
              isAiDrafted: saved.isAiDrafted,
            };
            // Keep the cached fetch in sync so any future re-mount
            // (e.g. React Strict Mode double-render in dev) sees the
            // up-to-date row instead of a stale one.
            queryClient.setQueryData(
              getGetReplyDraftQueryKey(kind, ref),
              saved,
            );
          },
        },
      );
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, body, isAiDrafted, open, enabled, kind, ref]);

  /**
   * Drop the saved draft on the server and reset local tracking.
   * Called from the explicit "Discard draft" button and from the
   * post-send cleanup path. Idempotent — if no row exists the server
   * returns 204 anyway.
   */
  const clearDraft = async () => {
    try {
      await deleteMutation.mutateAsync({ kind, ref });
    } catch {
      // Non-fatal — the draft will get overwritten on the next save
      // anyway. Don't surface an error to the user for cleanup.
    }
    lastSavedRef.current = null;
    queryClient.setQueryData(getGetReplyDraftQueryKey(kind, ref), null);
  };

  return {
    /** True once the initial GET has resolved (success or null). */
    restored: restoredRef.current,
    /** True when the latest debounced save is in flight. */
    saving: saveMutation.isPending,
    /** Server-side `updated_at` of the last successful save (or null). */
    lastSavedAt:
      saveMutation.data?.updatedAt ?? draftQuery.data?.updatedAt ?? null,
    clearDraft,
  };
}
